import { cx } from '../../ui/index.ts';
import type { LampTone } from '../../ui/index.ts';
import type { LegState } from './legPhysical.ts';

/**
 * The leg lamp vocabulary of #2658: solid red = removed, red outline =
 * removal record (mechanisms off), yellow = shadow / failing, orange
 * diamond = 金身, green = normal, grey outline = 未探测, solid grey =
 * manual, dim grey dot = takes no traffic. StatusLamp has no hollow or
 * diamond form, so this draws the same square with those variants.
 */

const TONE: Readonly<Record<LegState, LampTone>> = {
  ok: 'ok',
  failing: 'warn',
  shadow: 'warn',
  tripped: 'bad',
  record: 'bad',
  immune: 'signal',
  unprobed: 'off',
  manual: 'off',
};

const FACE: Readonly<Record<LegState, string>> = {
  ok: 'bg-ok shadow-lamp-ok',
  failing: 'bg-warn shadow-lamp-warn',
  shadow: 'bg-warn shadow-lamp-warn',
  tripped: 'bg-bad shadow-lamp-bad',
  record: 'bg-transparent ring-[1.5px] ring-inset ring-bad',
  immune: 'rotate-45 scale-[0.85] rounded-[1px] bg-signal shadow-lamp-signal',
  unprobed: 'bg-transparent ring-[1.5px] ring-inset ring-line-strong',
  manual: 'bg-line-strong',
};

export function legStateTone(state: LegState): LampTone {
  return TONE[state];
}

export function LegLampMark({ state, reachable = true, size = 'sm', title, testId }: {
  readonly state: LegState;
  /** False: the leg takes no traffic; drawn as a small dim dot whatever its probe state. */
  readonly reachable?: boolean;
  readonly size?: 'sm' | 'md';
  readonly title?: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <span
      aria-hidden={title === undefined ? true : undefined}
      role={title === undefined ? undefined : 'img'}
      aria-label={title}
      title={title}
      data-testid={testId}
      data-tone={TONE[state]}
      data-state={reachable ? state : 'dead'}
      className={cx(
        'inline-block shrink-0 rounded-[1.5px]',
        reachable
          ? cx(size === 'sm' ? 'h-1.5 w-1.5' : 'h-2 w-2', FACE[state])
          : 'h-[5px] w-[5px] bg-line opacity-90',
      )}
    />
  );
}
